import { Injectable } from "@angular/core";
import moment from "moment-timezone";
import * as _ from "lodash";

@Injectable({
  providedIn: "root",
})
export class CheckScheduleService {
  constructor() {}

  private get_moment(
    hour_minute: any,
    now: any,
    previous_day: any,
    timezone: any
  ) {
    const hour = hour_minute.substring(0, hour_minute.indexOf(":"));
    const minute = hour_minute.substring(hour_minute.indexOf(":") + 1);

    const moment_obj = moment
      .tz(now, timezone || "Europe/Athens")
      .hour(hour)
      .minute(minute)
      .startOf("minute");

    if (previous_day) moment_obj.subtract(1, "day");

    return moment_obj;
  }

  private is_in_time_period(
    time_period: any,
    now: any,
    previous_day: any,
    timezone: any
  ) {
    // console.log('time_period:', time_period);

    const start = this.get_moment(
      time_period.start,
      now,
      previous_day,
      timezone
    );
    const stop = this.get_moment(time_period.stop, now, previous_day, timezone);

    // if hour for stop < hour for start, we assume store closes after midnight
    if (stop.isSameOrBefore(start)) stop.add(1, "day");
    // console.log('start:', start);
    // console.log('stop:', stop);
    // console.log('now:', now);

    return now.isBetween(start, stop, null, "[)");
  }

  private is_store_online(
    schedule: any,
    timestamp: any,
    store_schedule: any,
    timezone: any
  ) {
    const main = (
      schedule: any,
      timestamp: any,
      store_schedule: any,
      ignore_store_schedule: any,
      timezone: any
    ) => {
      const now = timestamp
        ? moment.tz(parseInt(timestamp, 10), timezone || "Europe/Athens")
        : moment.tz(timezone || "Europe/Athens");
      let today = now.day();
      let previous_day = false;

      if (store_schedule && !ignore_store_schedule) {
        const store_schedule_info = main(
          store_schedule,
          timestamp,
          null,
          true,
          timezone
        );
        today = store_schedule_info.day;
      }

      if (
        schedule[today].all_day &&
        (schedule[today].available || schedule[today].active) &&
        !store_schedule
      ) {
        return {
          open: true,
          day: today,
          all_day: true,
          price: schedule[today].price,
        };
      } else {
        const today_index = _.findIndex(
          schedule[today].all_day &&
            (schedule[today].available || schedule[today].active) &&
            store_schedule
            ? store_schedule[today].time_periods
            : schedule[today].time_periods,
          (tp) => this.is_in_time_period(tp, now, previous_day, timezone)
        );
        // console.log('today index:', today_index);

        if (
          today_index !== -1 &&
          (schedule[today].available || schedule[today].active)
        ) {
          const timePeriods = store_schedule
            ? store_schedule[today].time_periods
            : schedule[today].time_periods;
          return {
            open: true,
            day: today,
            index: today_index,
            all_day: !!schedule[today].all_day,
            price: timePeriods?.[today_index]?.price ?? schedule[today].price,
          };
        } else {
          // for cases where we check "after midnight";
          // store found closed.
          // check previous day
          // console.log('checking for previous day');
          // TODO:
          const yesterday = moment
            .tz(now, timezone || "Europe/Athens")
            .subtract(1, "day")
            .day();
          previous_day = true;
          const yesterday_index = _.findIndex(
            schedule[yesterday].all_day &&
              (schedule[today].available || schedule[today].active) &&
              store_schedule
              ? store_schedule[today].time_periods
              : schedule[yesterday].time_periods,
            (tp) => this.is_in_time_period(tp, now, previous_day, timezone)
          );

          // console.log('yesterday index:', yesterday_index);
          if (yesterday_index !== -1) {
            // console.log('in, expect true');

            const timePeriods = store_schedule
              ? store_schedule[yesterday].time_periods
              : schedule[yesterday].time_periods;

            return {
              open: true,
              day: yesterday,
              index: yesterday_index,
              all_day: !!schedule[yesterday].all_day,
              price:
                timePeriods?.[yesterday_index]?.price ?? schedule[today].price,
            };
          } else {
            // console.log('schedule[today].active:', !!schedule[today].active);
            // console.log('schedule[today].available:', !!schedule[today].available);
            return {
              open: false,
              day: today,
              all_day: !!schedule[today].all_day,
              price: schedule[today].price,
            };
          }
        }
      }
    };

    return main(schedule, timestamp, store_schedule, false, timezone);
  }

  private format_schedule(schedule: any) {
    var mod = _.cloneDeep(schedule);
    // Schedule values are wall-clock seconds, not elapsed time on a DST date.
    // Like the previous moment arithmetic, negative values wrap around midnight
    // and non-numeric values read as midnight.
    const formatTime = (seconds: any) => {
      const parsed = parseInt(seconds, 10);
      const minutes =
        ((Math.floor((isNaN(parsed) ? 0 : parsed) / 60) % 1440) + 1440) % 1440;
      return (
        String(Math.floor(minutes / 60)).padStart(2, "0") +
        ":" +
        String(minutes % 60).padStart(2, "0")
      );
    };

    _.each(mod, (day_schedule, day, list) => {
      let formatted: any = [];
      // each day_schedule time period contains attributes start and stop
      _.each(day_schedule.time_periods, (time_period) => {
        formatted.push({
          ..._.omit(time_period, ["start", "stop"]), // preserve everything except start/stop
          start: formatTime(time_period.start),
          stop: formatTime(time_period.stop),
        });
      });

      list[day].time_periods = formatted;
    });
    return mod;
  }

  public checkSchedule(
    scheduleArray: any,
    timezone: any,
    forPriceSchedule: boolean
  ) {
    console.log("schedule array", scheduleArray);
    // Keep weekday indexes; inactive yesterday must not supply overnight hours.
    const schedule = _.range(7).map((day) => {
      const daySchedule: any =
        _.isObject(scheduleArray?.[day]) && !_.isArray(scheduleArray[day])
          ? scheduleArray[day]
          : {};
      return {
        ...daySchedule,
        time_periods:
          (daySchedule.active || daySchedule.available) &&
          _.isArray(daySchedule.time_periods)
            ? daySchedule.time_periods
            : [],
      };
    });

    if (forPriceSchedule) {
      return this.is_store_online(
        this.format_schedule(schedule),
        moment()
          .tz(timezone || "Europe/Athens")
          .format("x"),
        null,
        timezone || "Europe/Athens"
      );
    } else {
      return this.is_store_online(
        this.format_schedule(schedule),
        moment()
          .tz(timezone || "Europe/Athens")
          .format("x"),
        null,
        timezone || "Europe/Athens"
      ).open;
    }
  }
}
